import React, { useMemo, useState } from "react";
import {
  Drawer,
  Input,
  Button,
  Space,
  Tooltip,
  Empty,
  Tag,
  Popconfirm,
  Collapse,
  ColorPicker,
} from "antd";
import { colorForAnno } from "../utils/annotationShapes";
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  AimOutlined,
  DeleteOutlined,
  CaretRightOutlined,
  SolutionOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { GiMicroscope, GiMagnifyingGlass } from "react-icons/gi";
import { LiaXRaySolid } from "react-icons/lia";
import {
  MODALITY_LABEL,
  MODALITY_ORDER,
  getModalityFromKey,
  getSubLabelFromKey,
} from "../utils/annotationKey";

const MODALITY_ICONS = {
  macro: <GiMagnifyingGlass size={18} />,
  radio: <LiaXRaySolid size={18} />,
  histo: <GiMicroscope size={18} />,
};

const AuthorBadge = ({ role }) => {
  const isProf = role === "Professeur";
  return (
    <Tag color={isProf ? "geekblue" : "purple"} style={{ marginRight: 0 }}>
      {isProf ? "Prof" : "Élève"}
    </Tag>
  );
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
    });
  } catch {
    return "";
  }
};

const AnnotationCard = ({
  annotation,
  visible,
  canDelete,
  onToggle,
  onGoTo,
  onDelete,
  onSetColor,
}) => {
  return (
    <div
      style={{
        padding: "8px 10px",
        border: "1px solid var(--color-border)",
        borderRadius: 6,
        background: "var(--color-bg)",
        marginBottom: 8,
        opacity: visible ? 1 : 0.55,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontWeight: 500,
              color: "var(--color-text)",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {annotation.name}
          </div>
          {annotation.description && (
            <div
              style={{
                fontSize: 12,
                color: "var(--color-text-secondary)",
                marginTop: 2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {annotation.description}
            </div>
          )}
          <Space size={6} style={{ marginTop: 6 }}>
            <AuthorBadge role={annotation.authorRole} />
            {annotation.createdAt && (
              <span
                style={{
                  fontSize: 11,
                  color: "var(--color-text-secondary)",
                }}
              >
                {formatDate(annotation.createdAt)}
              </span>
            )}
          </Space>
        </div>
        <Space size={2}>
          {canDelete && onSetColor && (
            <Tooltip title="Couleur">
              <ColorPicker
                size="small"
                value={colorForAnno(annotation)}
                disabledAlpha
                onChangeComplete={(c) => onSetColor(c.toRgbString())}
              />
            </Tooltip>
          )}
          <Tooltip title={visible ? "Masquer" : "Afficher"}>
            <Button
              type="text"
              size="small"
              icon={visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
              onClick={() => onToggle(!visible)}
            />
          </Tooltip>
          <Tooltip title="Aller à">
            <Button
              type="text"
              size="small"
              icon={<AimOutlined />}
              onClick={onGoTo}
            />
          </Tooltip>
          {canDelete && (
            <Popconfirm
              title="Supprimer cette annotation ?"
              okText="Supprimer"
              cancelText="Annuler"
              okButtonProps={{ danger: true }}
              onConfirm={onDelete}
            >
              <Tooltip title="Supprimer">
                <Button type="text" size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      </div>
    </div>
  );
};

const AnnotationsDrawer = ({
  open,
  onClose,
  visibleAnnotationsByKey,
  sectionVisibility,
  annotationVisibilityById,
  authorsByRole = { Professeur: {}, Etudiant: {} },
  authorVisibility = {},
  onToggleAuthor,
  userRole,
  onGoTo,
  onDelete,
  onToggleSection,
  onToggleAnnotation,
  onSetColor,
  onShowAll,
  onHideAll,
}) => {
  const [search, setSearch] = useState("");

  // Group keys by modality
  const groupedKeys = useMemo(() => {
    const map = { macro: [], radio: [], histo: [] };
    Object.keys(visibleAnnotationsByKey).forEach((key) => {
      const modality = getModalityFromKey(key);
      if (modality && map[modality]) map[modality].push(key);
    });
    return map;
  }, [visibleAnnotationsByKey]);

  const filterAnno = (a) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      a.name?.toLowerCase().includes(q) ||
      a.description?.toLowerCase().includes(q)
    );
  };

  const totalVisible = useMemo(
    () =>
      Object.values(visibleAnnotationsByKey).reduce(
        (sum, list) => sum + (list?.length || 0),
        0
      ),
    [visibleAnnotationsByKey]
  );

  const renderModalitySection = (modality) => {
    const keys = groupedKeys[modality] || [];
    const annosByKey = keys.map((key) => ({
      key,
      list: (visibleAnnotationsByKey[key] || []).filter(filterAnno),
    }));
    const totalForModality = annosByKey.reduce(
      (s, { list }) => s + list.length,
      0
    );

    if (totalForModality === 0 && search) return null;

    // For radiology there are multiple series — render a sub-list per series.
    // For macro/histo there's only one key → flat list.
    const allModalityKeys = keys;
    const allOn = allModalityKeys.every(
      (k) => sectionVisibility[k] !== false
    );

    return (
      <Collapse.Panel
        key={modality}
        header={
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {MODALITY_ICONS[modality]}
            <span style={{ fontWeight: 500 }}>{MODALITY_LABEL[modality]}</span>
            <Tag style={{ marginLeft: 4 }}>{totalForModality}</Tag>
          </span>
        }
        extra={
          totalForModality > 0 && (
            <Tooltip title={allOn ? "Masquer la section" : "Afficher la section"}>
              <Button
                type="text"
                size="small"
                icon={allOn ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  allModalityKeys.forEach((k) => onToggleSection(k, !allOn));
                }}
              />
            </Tooltip>
          )
        }
      >
        {totalForModality === 0 ? (
          <div style={{ color: "var(--color-text-secondary)", fontSize: 12 }}>
            Aucune annotation
          </div>
        ) : (
          annosByKey.map(({ key, list }) => {
            if (list.length === 0) return null;
            const subLabel = getSubLabelFromKey(key);
            return (
              <div key={key} style={{ marginBottom: 8 }}>
                {subLabel && (
                  <div
                    style={{
                      fontSize: 11,
                      textTransform: "uppercase",
                      color: "var(--color-text-secondary)",
                      marginBottom: 6,
                      letterSpacing: 0.4,
                    }}
                  >
                    {subLabel}
                  </div>
                )}
                {list.map((anno) => (
                  <AnnotationCard
                    key={anno.id}
                    annotation={anno}
                    visible={annotationVisibilityById[anno.id] !== false}
                    canDelete={anno.authorRole === userRole}
                    onToggle={(v) => onToggleAnnotation(anno.id, v)}
                    onGoTo={() => onGoTo(key, anno.id)}
                    onDelete={() => onDelete(key, anno.id)}
                    onSetColor={(color) => onSetColor?.(key, anno.id, color)}
                  />
                ))}
              </div>
            );
          })
        )}
      </Collapse.Panel>
    );
  };

  const ROLE_GROUPS = [
    { role: "Professeur", label: "Annotations par professeur", icon: <SolutionOutlined /> },
    { role: "Etudiant", label: "Annotations par étudiant", icon: <TeamOutlined /> },
  ];

  const renderAuthorSection = () => {
    const groups = ROLE_GROUPS.filter(
      (g) => Object.keys(authorsByRole[g.role] || {}).length > 0
    );
    if (groups.length === 0) return null;
    return (
      <Collapse
        ghost
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} />
        )}
        style={{
          marginBottom: 12,
          borderBottom: "1px solid var(--color-border)",
          paddingBottom: 4,
        }}
      >
        {groups.map((g) => {
          const authors = authorsByRole[g.role] || {};
          const names = Object.keys(authors).sort();
          const total = names.reduce((s, n) => s + authors[n], 0);
          return (
            <Collapse.Panel
              key={g.role}
              header={
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {g.icon}
                  <span style={{ fontWeight: 500 }}>{g.label}</span>
                  <Tag style={{ marginLeft: 4 }}>{total}</Tag>
                </span>
              }
            >
              {names.map((name) => {
                const visible = authorVisibility[name] !== false;
                return (
                  <div
                    key={name}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      padding: "2px 4px",
                    }}
                  >
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        minWidth: 0,
                      }}
                    >
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 220,
                        }}
                      >
                        {name}
                      </span>
                      <Tag>{authors[name]}</Tag>
                    </span>
                    <Tooltip title={visible ? "Masquer" : "Afficher"}>
                      <Button
                        type="text"
                        size="small"
                        icon={visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                        onClick={() => onToggleAuthor?.(name, !visible)}
                      />
                    </Tooltip>
                  </div>
                );
              })}
            </Collapse.Panel>
          );
        })}
      </Collapse>
    );
  };

  return (
    <Drawer
      title={
        <Space>
          <span>Annotations</span>
          <Tag>{totalVisible}</Tag>
        </Space>
      }
      placement="right"
      open={open}
      onClose={onClose}
      width={400}
      mask={false}
      styles={{ body: { padding: 12 } }}
    >
      <Space.Compact style={{ width: "100%", marginBottom: 12 }}>
        <Input
          placeholder="Rechercher..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
        />
      </Space.Compact>
      <Space style={{ marginBottom: 12 }} wrap>
        <Button size="small" icon={<EyeOutlined />} onClick={onShowAll}>
          Tout afficher
        </Button>
        <Button
          size="small"
          icon={<EyeInvisibleOutlined />}
          onClick={onHideAll}
        >
          Tout masquer
        </Button>
      </Space>

      {totalVisible > 0 && renderAuthorSection()}

      {totalVisible === 0 ? (
        <Empty description="Aucune annotation" />
      ) : (
        <Collapse
          defaultActiveKey={MODALITY_ORDER}
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
          ghost
        >
          {MODALITY_ORDER.map(renderModalitySection)}
        </Collapse>
      )}
    </Drawer>
  );
};

export default AnnotationsDrawer;
